import "../../kendo.data.js"; // Stiil required until kendo.data is modularized
import { IKendoJQueryStatic } from "../../core/index.js";
import { utilsService } from "../../core/services/utils.service.js";
import type { IMessage, IChatOptions } from "../models/index.js";
import type { IFile } from "../../upload";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

/**
 * Data source schema configuration for chat messages.
 */
interface IDataSourceSchema {
    model: {
        id: string;
        fields: Record<string, {
            type?: string;
            defaultValue?: any;
            from?: string;
            parse?: (value: any) => any;
        }>;
    };
}

/**
 * Internal options for the data collaborator.
 */
interface IDataManagerOptions {
    autoSync: boolean;
    autoAssignId?: boolean;
    schema: IDataSourceSchema;
    data?: any[];
}

export interface IChatDataContext {
    chatOptions: Partial<IChatOptions>;
}

/**
 * DataManager handles all data operations for the Chat component.
 * Manages message storage, retrieval, updates, and data source configuration.
 *
 * This is an internal collaborator - not a shared service.
 */
export class DataManager {
    /** Default configuration options */
    private static readonly DEFAULT_OPTIONS: IDataManagerOptions = {
        autoSync: true,
        schema: {
            model: {
                id: "id",
                fields: {
                    id: { type: "string" },
                    text: { type: "string" },
                    authorId: { type: "string" },
                    authorName: { type: "string" },
                    authorImageUrl: { type: "string" },
                    authorImageAltText: { type: "string" },
                    replyToId: { type: "string", defaultValue: null },
                    isDeleted: { type: "boolean", defaultValue: false },
                    isPinned: { type: "boolean", defaultValue: false },
                    isTyping: { type: "boolean", defaultValue: false },
                    timestamp: { type: "date" },
                    files: { parse: (value: any) => value || [] },
                    suggestedActions: { parse: (value: any) => value || [] },
                    status: { type: "string", defaultValue: null },
                    failed: { type: "boolean", defaultValue: false },
                    attachments: { parse: (value: any) => value || null },
                    attachmentLayout: { type: "string", defaultValue: null }
                }
            }
        }
    };

    /** Field mapping from chat options to data source fields */
    private static readonly FIELD_MAP: Record<string, string> = {
        text: "textField",
        authorId: "authorIdField",
        authorName: "authorNameField",
        authorImageUrl: "authorImageUrlField",
        authorImageAltText: "authorImageAltTextField",
        id: "idField",
        timestamp: "timestampField",
        files: "filesField",
        suggestedActions: "suggestedActionsField",
        replyToId: "replyToIdField",
        isDeleted: "isDeletedField",
        isPinned: "isPinnedField",
        isTyping: "isTypingField",
        status: "statusField",
        failed: "failedField",
        attachments: "attachmentsField",
        attachmentLayout: "attachmentLayoutField"
    };

    private dataSource: any; // kendo.data.DataSource
    private options: IDataManagerOptions;
    private context: IChatDataContext;

    /**
     * Constructs a new DataManager instance.
     * @param context - Context with chat options and services
     */
    constructor(context: IChatDataContext) {
        this.context = context;
        this.options = this.buildOptions(context.chatOptions);
        this.dataSource = this.createDataSource(context.chatOptions);
    }

    /**
     * Builds internal options from chat options.
     */
    private buildOptions(chatOptions: Partial<IChatOptions>): IDataManagerOptions {
        let options: IDataManagerOptions;

        if (chatOptions.dataSource instanceof kendo.data.DataSource) {
            options = $.extend(true, {}, DataManager.DEFAULT_OPTIONS, chatOptions.dataSource.options);
        } else if (Array.isArray(chatOptions.dataSource)) {
            options = $.extend(true, {}, DataManager.DEFAULT_OPTIONS, { data: chatOptions.dataSource });
        } else {
            options = $.extend(true, {}, DataManager.DEFAULT_OPTIONS, chatOptions.dataSource);
        }

        options.autoAssignId = chatOptions.autoAssignId;
        this.mapFields(options.schema.model.fields, chatOptions);

        return options;
    }

    /**
     * Creates and configures a data source for the chat component.
     */
    private createDataSource(chatOptions: Partial<IChatOptions>): any {
        if (chatOptions.dataSource instanceof kendo.data.DataSource) {
            return chatOptions.dataSource;
        }

        return kendo.data.DataSource.create(this.options);
    }

    /**
     * Maps field configuration from chat options to data source field definitions.
     */
    private mapFields(
        fields: Record<string, any>,
        chatOptions: Partial<IChatOptions>
    ): void {
        for (const key in fields) {
            if (Object.prototype.hasOwnProperty.call(fields, key) && DataManager.FIELD_MAP[key]) {
                const optionKey = DataManager.FIELD_MAP[key] as keyof IChatOptions;
                fields[key].from = chatOptions[optionKey];
            }
        }
    }

    /**
     * Gets the data source instance.
     */
    getDataSource(): any {
        return this.dataSource;
    }

    /**
     * Gets the current data source view.
     */
    getView(): IMessage[] {
        return this.dataSource.view();
    }

    /**
     * Gets the last message in the data source view.
     */
    getLastMessage(): IMessage | null {
        const view = this.getView();
        return view.length ? view[view.length - 1] : null;
    }

    /**
     * Gets a message by ID from the data source.
     */
    getMessageById(id: string | number): IMessage | null {
        if (!id) {
            return null;
        }
        return this.dataSource.get(id);
    }

    /**
     * Gets a message by UID from the data source.
     */
    getMessageByUid(uid: string): IMessage | null {
        return this.dataSource.getByUid(uid);
    }

    /**
     * Gets a file by UID from a message's files array.
     */
    getFileByUid(message: IMessage, uid: string): IFile | null {
        if (!message || !uid) {
            return null;
        }
        return message.files.find(file => file.uid === uid) || null;
    }

    /**
     * Gets the currently pinned message.
     */
    getPinnedMessage(): IMessage | null {
        return this.dataSource.data().find((m: IMessage) => m.isPinned) || null;
    }

    /**
     * Adds a new message to the data source.
     */
    postMessage(message: string | Partial<IMessage>, currentUserId: string): IMessage {
        const messageInput = typeof message === "string" ? { text: message } : message;

        const messageData: Partial<IMessage> = {
            id: this.options.autoAssignId ? utilsService.guid() : undefined,
            timestamp: new Date(),
            files: [],
            ...messageInput,
            authorId: messageInput.authorId?.toString() || currentUserId
        };

        return this.dataSource.add(messageData);
    }

    /**
     * Updates a message in the data source.
     */
    updateMessage(message: IMessage | Partial<IMessage>, newData: Partial<IMessage>): IMessage | null {
        if (!message) {
            return null;
        }

        let targetMessage = message;
        if (!(message instanceof kendo.data.ObservableObject)) {
            targetMessage = this.getMessageById((message as IMessage).id)!;
        }

        if (!targetMessage) {
            return null;
        }

        for (const key in newData) {
            if (Object.prototype.hasOwnProperty.call(newData, key)) {
                (targetMessage as any).set(key, (newData as any)[key]);
            }
        }

        return targetMessage as IMessage;
    }

    /**
     * Marks a message as deleted.
     */
    removeMessage(message: IMessage): boolean {
        if (!message) {
            return false;
        }

        (message as any).set("isDeleted", true);
        return true;
    }

    /**
     * Pins a message.
     */
    pinMessage(message: IMessage): IMessage | false {
        if (!message) {
            return false;
        }

        // Unpin any currently pinned message
        const pinnedMessage = this.getPinnedMessage();
        if (pinnedMessage) {
            (pinnedMessage as any).set("isPinned", false);
        }

        (message as any).set("isPinned", true);
        return message;
    }

    /**
     * Clears the currently pinned message.
     */
    clearPinnedMessage(): void {
        const pinnedMessage = this.getPinnedMessage();
        if (pinnedMessage) {
            (pinnedMessage as any).set("isPinned", false);
        }
    }
}
